import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, MapPin, Calendar, Scale, Ruler, Euro, ArrowRight, Loader2, Sparkles, Info, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MatchingTripsSection } from '../../components/MatchingTrips';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { parcelsApi, matchingApi } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { toast } from 'sonner';

const NewParcelPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [suggestedTrips, setSuggestedTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [formData, setFormData] = useState({
    weight: '',
    length: '',
    width: '',
    height: '',
    pickup_street: '',
    pickup_city: '',
    pickup_postal_code: '',
    pickup_country: '',
    delivery_street: '',
    delivery_city: '',
    delivery_postal_code: '',
    delivery_country: '',
    pickup_date: '',
    description: '',
    payment_method: 'online',
    proposed_price: '', // Budget proposé par l'expéditeur
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // Calculer le prix suggéré basé sur les trajets trouvés
  const suggestedPrice = suggestedTrips.length > 0 
    ? Math.min(...suggestedTrips.map(t => t.price_per_kg * (parseFloat(formData.weight) || 5)))
    : null;

  // Debounced search for matching trips
  const searchMatchingTrips = useCallback(async () => {
    if (formData.pickup_city.length >= 2 && formData.delivery_city.length >= 2) {
      setLoadingTrips(true);
      try {
        const params = {
          departure_city: formData.pickup_city,
          arrival_city: formData.delivery_city,
        };
        if (formData.weight) params.weight = parseFloat(formData.weight);
        if (formData.pickup_date) params.date = formData.pickup_date;
        
        const response = await matchingApi.suggestTrips(params);
        setSuggestedTrips(response.data);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      } finally {
        setLoadingTrips(false);
      }
    } else {
      setSuggestedTrips([]);
    }
  }, [formData.pickup_city, formData.delivery_city, formData.weight, formData.pickup_date]);

  // Trigger search when cities change
  useEffect(() => {
    const timer = setTimeout(() => {
      searchMatchingTrips();
    }, 500); // Debounce 500ms
    
    return () => clearTimeout(timer);
  }, [formData.pickup_city, formData.delivery_city, formData.weight, formData.pickup_date, searchMatchingTrips]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.proposed_price || parseFloat(formData.proposed_price) <= 0) {
      toast.error('Veuillez indiquer votre budget');
      return;
    }
    
    setLoading(true);

    try {
      const parcelData = {
        weight: parseFloat(formData.weight),
        dimensions: {
          length: parseFloat(formData.length),
          width: parseFloat(formData.width),
          height: parseFloat(formData.height),
        },
        pickup_address: {
          street: formData.pickup_street,
          city: formData.pickup_city,
          postal_code: formData.pickup_postal_code,
          country: formData.pickup_country || '',
        },
        delivery_address: {
          street: formData.delivery_street,
          city: formData.delivery_city,
          postal_code: formData.delivery_postal_code,
          country: formData.delivery_country || '',
        },
        pickup_date: formData.pickup_date,
        description: formData.description,
        payment_method: formData.payment_method,
        proposed_price: parseFloat(formData.proposed_price),
      };

      const response = await parcelsApi.create(parcelData);
      toast.success('Demande d\'envoi créée avec succès!');
      navigate(`/dashboard/parcel/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleContactCarrier = (trip) => {
    // Navigate to search trips with pre-filled data
    navigate(`/dashboard/search-trips?departure=${formData.pickup_city}&arrival=${formData.delivery_city}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="new-parcel-page">
      <div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Nouvel envoi
        </h1>
        <p className="text-slate-600 mt-1">Créez une demande d&apos;enlèvement pour votre colis</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Parcel Details */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-500" />
              Détails du colis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="weight" className="flex items-center gap-1">
                  <Scale className="w-4 h-4 text-slate-400" />
                  Poids (kg)
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  placeholder="5"
                  value={formData.weight}
                  onChange={(e) => updateField('weight', e.target.value)}
                  required
                  data-testid="weight-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="length" className="flex items-center gap-1">
                  <Ruler className="w-4 h-4 text-slate-400" />
                  Longueur (cm)
                </Label>
                <Input
                  id="length"
                  type="number"
                  placeholder="40"
                  value={formData.length}
                  onChange={(e) => updateField('length', e.target.value)}
                  required
                  data-testid="length-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="width">Largeur (cm)</Label>
                <Input
                  id="width"
                  type="number"
                  placeholder="30"
                  value={formData.width}
                  onChange={(e) => updateField('width', e.target.value)}
                  required
                  data-testid="width-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="height">Hauteur (cm)</Label>
                <Input
                  id="height"
                  type="number"
                  placeholder="20"
                  value={formData.height}
                  onChange={(e) => updateField('height', e.target.value)}
                  required
                  data-testid="height-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optionnel)</Label>
              <Textarea
                id="description"
                placeholder="Décrivez le contenu du colis..."
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                className="min-h-[80px]"
                data-testid="description-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Pickup Address */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-500" />
              Adresse d&apos;enlèvement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressAutocomplete
              label="Rechercher une adresse"
              placeholder="Tapez une adresse (ex: 10 rue de la Paix, Paris)"
              onAddressSelect={(address) => {
                if (address) {
                  setFormData({
                    ...formData,
                    pickup_street: address.street || '',
                    pickup_city: address.city || '',
                    pickup_postal_code: address.postal_code || '',
                    pickup_country: address.country || ''
                  });
                }
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup_street">Rue</Label>
                <Input
                  id="pickup_street"
                  placeholder="123 rue de la Paix"
                  value={formData.pickup_street}
                  onChange={(e) => updateField('pickup_street', e.target.value)}
                  required
                  data-testid="pickup-street-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickup_city">Ville</Label>
                <Input
                  id="pickup_city"
                  placeholder="Paris"
                  value={formData.pickup_city}
                  onChange={(e) => updateField('pickup_city', e.target.value)}
                  required
                  data-testid="pickup-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickup_postal_code">Code postal</Label>
                <Input
                  id="pickup_postal_code"
                  placeholder="75001"
                  value={formData.pickup_postal_code}
                  onChange={(e) => updateField('pickup_postal_code', e.target.value)}
                  required
                  data-testid="pickup-postal-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Address */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <MapPin className="w-5 h-5 text-red-500" />
              Adresse de livraison
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressAutocomplete
              label="Rechercher une adresse"
              placeholder="Tapez une adresse (ex: 5 place Bellecour, Lyon)"
              onAddressSelect={(address) => {
                if (address) {
                  setFormData({
                    ...formData,
                    delivery_street: address.street || '',
                    delivery_city: address.city || '',
                    delivery_postal_code: address.postal_code || '',
                    delivery_country: address.country || ''
                  });
                }
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="delivery_street">Rue</Label>
                <Input
                  id="delivery_street"
                  placeholder="456 avenue des Champs"
                  value={formData.delivery_street}
                  onChange={(e) => updateField('delivery_street', e.target.value)}
                  required
                  data-testid="delivery-street-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_city">Ville</Label>
                <Input
                  id="delivery_city"
                  placeholder="Lyon"
                  value={formData.delivery_city}
                  onChange={(e) => updateField('delivery_city', e.target.value)}
                  required
                  data-testid="delivery-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delivery_postal_code">Code postal</Label>
                <Input
                  id="delivery_postal_code"
                  placeholder="69001"
                  value={formData.delivery_postal_code}
                  onChange={(e) => updateField('delivery_postal_code', e.target.value)}
                  required
                  data-testid="delivery-postal-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pickup Date & Payment */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              Date et paiement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickup_date">Date d&apos;enlèvement souhaitée</Label>
                <Input
                  id="pickup_date"
                  type="date"
                  value={formData.pickup_date}
                  onChange={(e) => updateField('pickup_date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  required
                  data-testid="pickup-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_method" className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4 text-slate-400" />
                  Mode de paiement
                </Label>
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <CreditCard className="w-5 h-5 text-blue-500" />
                  <span className="text-slate-700 font-medium">Paiement sécurisé par carte bancaire (Stripe)</span>
                </div>
                <p className="text-xs text-slate-500">Le paiement est sécurisé et traité par Stripe</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Budget proposé */}
        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] text-xl font-bold text-slate-900 uppercase flex items-center gap-2">
              <Euro className="w-5 h-5 text-orange-500" />
              Votre budget
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="proposed_price">Combien êtes-vous prêt à payer ? *</Label>
              <div className="relative">
                <Input
                  id="proposed_price"
                  type="number"
                  min="1"
                  step="0.5"
                  placeholder="Ex: 25"
                  value={formData.proposed_price}
                  onChange={(e) => updateField('proposed_price', e.target.value)}
                  className="pl-8 text-lg font-semibold"
                  required
                  data-testid="proposed-price-input"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">€</span>
              </div>
              <p className="text-xs text-slate-500">
                Les cotransporteurs verront votre budget et pourront vous faire des offres
              </p>
            </div>
            
            {suggestedPrice && (
              <div className="bg-white rounded-lg p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700">Prix des cotransporteurs sur ce trajet</span>
                </div>
                <p className="text-sm text-slate-600">
                  À partir de <span className="font-bold text-green-600">{formatPrice(suggestedPrice)}</span> pour {formData.weight || 5} kg
                </p>
                {formData.proposed_price && parseFloat(formData.proposed_price) < suggestedPrice && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Votre budget est inférieur aux prix des cotransporteurs disponibles
                  </p>
                )}
                {formData.proposed_price && parseFloat(formData.proposed_price) >= suggestedPrice && (
                  <p className="text-xs text-green-600 mt-1">
                    ✅ Votre budget correspond aux prix du marché
                  </p>
                )}
              </div>
            )}
            
            {formData.proposed_price && (
              <div className="bg-slate-100 rounded-lg p-4">
                <p className="text-sm text-slate-600">
                  Commission plateforme (15%): <span className="font-medium">{formatPrice(parseFloat(formData.proposed_price) * 0.15)}</span>
                </p>
                <p className="text-lg font-bold text-slate-900 mt-1">
                  Total à payer: {formatPrice(parseFloat(formData.proposed_price) * 1.15)}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4" data-testid="submit-buttons">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-slate-300"
            data-testid="cancel-parcel-btn"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.proposed_price}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide px-8"
            data-testid="submit-parcel-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Création...
              </>
            ) : (
              <>
                Créer la demande
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </Button>
        </div>

        {/* Matching Trips - Auto suggestions */}
        {(formData.pickup_city.length >= 2 && formData.delivery_city.length >= 2) && (
          <MatchingTripsSection
            trips={suggestedTrips}
            loading={loadingTrips}
            title="Cotransporteurs disponibles"
            subtitle={`${suggestedTrips.length} cotransporteur${suggestedTrips.length > 1 ? 's' : ''} font déjà ce trajet`}
            onContact={handleContactCarrier}
            emptyMessage="Aucun cotransporteur ne fait ce trajet actuellement, mais votre demande sera visible par tous"
          />
        )}
      </form>
    </div>
  );
};

export default NewParcelPage;
